import { API_CONFIG } from '@Web/config/api.config';
import { RestClient } from '@Web/clients/RestClient';
import { clearGameSession, getGameSessionId } from '@core/utils/gameSession';
import { resolveAccessToken } from '@core/utils/resolveAccessToken';
import { formatCentsToCurrency } from '@core/utils/CurrencyFormatter';

export interface BetHistoryRound {
  roundId: string;
  betAmount: number;
  /** Display multiplier: cash-out X for wins, bust X for losses. */
  multiplier: number;
  /** Round crash / flew-away multiplier (`bustX`), independent of cash-out. */
  crashMultiplier: number;
  cashOutAmount: number | null;
  startBalance: number | null;
  endBalance: number | null;
  timestamp: number;
  serverSeed?: string;
  clientSeed?: string;
  serverSeedHash?: string;
  tableNo?: number;
  roundNo?: number;
  betSlot?: number;
  isBusted?: boolean;
}

/** One My Bets group: server round summary + the nested `bets` array. */
export interface BetHistoryGroup {
  roundId: string;
  roundNo?: number;
  timestamp: number;
  startBalance: number | null;
  endBalance: number | null;
  totalBet: number | null;
  totalWin: number | null;
  tableNo?: number;
  bets: BetHistoryRound[];
}

type RawRound = Record<string, unknown>;

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toMultiplier(value: unknown): number | null {
  const n = toNumber(value);
  if (n == null || n <= 0) return null;

  if (n < 100) {
    return n;
  }

  if (Number.isInteger(n) && n >= 100_000) {
    return n / 100_000;
  }

  if (Number.isInteger(n) && n >= 10_000 && n < 100_000) {
    const scaled = n / 10_000;
    if (scaled >= 1) return scaled;
  }

  if (Number.isInteger(n) && n >= 100 && n < 10_000) {
    const scaled = n / 100;
    if (scaled >= 1 && scaled <= 10_000) return scaled;
  }

  return n;
}

function decodeCashoutAtmbpMultiplier(value: unknown): number | null {
  const n = toNumber(value);
  if (n == null || n <= 0) return null;

  if (!Number.isInteger(n)) {
    return n;
  }

  if (n < 100) {
    return n;
  }

  if (n >= 100_000) {
    if (n % 1_000 === 0) {
      const scaled = n / 1_000;
      if (scaled >= 1 && scaled <= 10_000) return scaled;
    }
    return n / 100_000;
  }

  if (n >= 1_000) {
    const scaled = n / 1_000;
    if (scaled >= 1) return scaled;
  }

  if (n >= 100) {
    const scaled = n / 100;
    if (scaled >= 1 && scaled <= 10_000) return scaled;
  }

  return n;
}

function parseTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
}

function resolveCashoutMultiplier(
  item: RawRound,
  betAmount = 0,
  resolvedCashOut: number | null = null
): number | null {
  const atmbpCandidates = [item.atmbp, item.atMbp, item.atmBp];
  for (const candidate of atmbpCandidates) {
    const decoded = decodeCashoutAtmbpMultiplier(candidate);
    if (decoded != null && decoded > 0) {
      return decoded;
    }
  }

  const otherCandidates = [item.atX, item.cashedAtX, item.cashoutMul];
  for (const candidate of otherCandidates) {
    const decoded = toMultiplier(candidate);
    if (decoded != null && decoded > 0) {
      return decoded;
    }
  }

  if (betAmount > 0 && resolvedCashOut != null && resolvedCashOut > 0) {
    return resolvedCashOut / betAmount;
  }

  return null;
}

function resolveBalanceAmount(item: RawRound, keys: string[]): number | null {
  for (const key of keys) {
    const raw = toNumber(item[key]);
    if (raw != null) {
      return formatCentsToCurrency(raw);
    }
  }
  return null;
}

function resolveBetHistoryBalances(
  item: RawRound,
  betAmount: number,
  cashOutAmount: number | null
): { startBalance: number | null; endBalance: number | null } {
  const startKeys = [
    'startBalance',
    'balanceBefore',
    'openingBalance',
    'preBalance',
    'walletBefore',
    'balanceStart',
    'initialBalance',
    'balanceAtStart',
    'startWalletBalance',
    'balancePrior',
  ];
  const endKeys = [
    'endBalance',
    'balanceAfter',
    'closingBalance',
    'postBalance',
    'walletAfter',
    'balanceEnd',
    'finalBalance',
    'balanceAtEnd',
    'endWalletBalance',
    'balancePost',
  ];

  let startBalance = resolveBalanceAmount(item, startKeys);
  let endBalance = resolveBalanceAmount(item, endKeys);

  const payout = cashOutAmount ?? 0;

  if (startBalance == null && endBalance != null && betAmount > 0) {
    startBalance = endBalance + betAmount - payout;
  }
  if (endBalance == null && startBalance != null && betAmount > 0) {
    endBalance = startBalance - betAmount + payout;
  }

  return { startBalance, endBalance };
}

function isNestedRoundPayload(item: RawRound): boolean {
  return Array.isArray(item.bets);
}

/** New rounds API sends currency units and human multipliers (1.17, 1000.00). */
function normalizeNestedBet(
  round: RawRound,
  bet: RawRound
): BetHistoryRound | null {
  const roundId = round.roundId ?? round.roundNo ?? round.id;
  if (roundId == null) return null;

  const betAmount = toNumber(bet.bet) ?? toNumber(round.totalBet) ?? 0;
  const cashoutRaw = toNumber(bet.cashout) ?? toNumber(bet.cashOut);
  const cashOutAmount =
    cashoutRaw != null && cashoutRaw > 0 ? cashoutRaw : null;
  const cashoutMultiplier = toNumber(bet.multiplier);
  const hasCashout =
    cashOutAmount != null ||
    (cashoutMultiplier != null && cashoutMultiplier > 0);
  const crashMultiplier =
    toNumber(bet.bustX) ??
    toNumber(bet.crashX) ??
    toNumber(round.bustX) ??
    toNumber(round.crashX) ??
    0;
  // No cashout during an in-flight round is not a bust — wait for crash X.
  const isBusted = !hasCashout && crashMultiplier > 0;

  const multiplier = hasCashout
    ? cashoutMultiplier != null && cashoutMultiplier > 0
      ? cashoutMultiplier
      : betAmount > 0 && cashOutAmount != null
        ? cashOutAmount / betAmount
        : 0
    : 0;

  return {
    roundId: String(roundId),
    betAmount,
    multiplier,
    crashMultiplier: crashMultiplier > 0 ? crashMultiplier : 0,
    cashOutAmount,
    startBalance: toNumber(round.startBalance),
    endBalance: toNumber(round.endBalance),
    timestamp: parseTimestamp(
      bet.playedAt ??
        round.playedAt ??
        round.createdAt ??
        round.endedAt ??
        round.timestamp
    ),
    tableNo: toNumber(round.tableNo) ?? undefined,
    roundNo: toNumber(round.roundNo) ?? undefined,
    betSlot: toNumber(bet.slot) ?? toNumber(bet.betSlot) ?? undefined,
    isBusted,
  };
}

function normalizeLegacyRound(item: RawRound): BetHistoryRound | null {
  const roundId = item.roundId ?? item.roundNo ?? item.id;
  if (roundId == null) return null;

  const rawBetAmount =
    toNumber(item.betAmount) ??
    toNumber(item.stake) ??
    toNumber(item.bet) ??
    toNumber(item.wager) ??
    0;
  const betAmount = formatCentsToCurrency(rawBetAmount);

  const winAmount = toNumber(item.winAmount);
  const bonusWinAmount = toNumber(item.bonusWinAmount) ?? 0;
  const totalWinRaw =
    winAmount != null && winAmount > 0
      ? winAmount + (bonusWinAmount > 0 ? bonusWinAmount : 0)
      : winAmount;

  const cashOutAmount =
    toNumber(item.cashOutAmount) ??
    toNumber(item.cashoutAmount) ??
    toNumber(item.payout) ??
    toNumber(item.win) ??
    toNumber(item.cashOut) ??
    (totalWinRaw != null && totalWinRaw > 0 ? totalWinRaw : null);

  const resolvedCashOut =
    cashOutAmount != null && cashOutAmount > 0
      ? formatCentsToCurrency(cashOutAmount)
      : null;

  const cashoutMultiplier = resolveCashoutMultiplier(
    item,
    betAmount,
    resolvedCashOut
  );
  const resolvedCashOutWithMultiplier =
    resolvedCashOut ??
    (cashoutMultiplier != null && cashoutMultiplier > 0 && betAmount > 0
      ? betAmount * cashoutMultiplier
      : null);

  const bustMultiplier =
    toMultiplier(item.bustX) ??
    toMultiplier(item.crashX) ??
    toMultiplier(item.bust) ??
    toMultiplier(item.roundMultiplier) ??
    toMultiplier(item.multiplier);

  const hasCashout =
    (cashoutMultiplier != null && cashoutMultiplier > 0) ||
    (resolvedCashOutWithMultiplier != null &&
      resolvedCashOutWithMultiplier > 0);

  const multiplier = hasCashout
    ? ((cashoutMultiplier != null && cashoutMultiplier > 0
        ? cashoutMultiplier
        : betAmount > 0 && resolvedCashOutWithMultiplier != null
          ? resolvedCashOutWithMultiplier / betAmount
          : bustMultiplier) ?? 0)
    : (bustMultiplier ?? 0);

  const { startBalance, endBalance } = resolveBetHistoryBalances(
    item,
    betAmount,
    resolvedCashOutWithMultiplier
  );

  return {
    roundId: String(roundId),
    betAmount,
    multiplier,
    crashMultiplier: bustMultiplier ?? 0,
    cashOutAmount: resolvedCashOutWithMultiplier,
    startBalance,
    endBalance,
    timestamp: parseTimestamp(
      item.playedAt ??
        item.createdAt ??
        item.endedAt ??
        item.timestamp ??
        item.timeStamp
    ),
    serverSeed:
      typeof item.serverSeed === 'string' ? item.serverSeed : undefined,
    clientSeed:
      typeof item.clientSeed === 'string' ? item.clientSeed : undefined,
    serverSeedHash:
      typeof item.serverSeedHash === 'string' ? item.serverSeedHash : undefined,
    tableNo: toNumber(item.tableNo) ?? undefined,
    roundNo: toNumber(item.roundNo) ?? toNumber(item.roundId) ?? undefined,
    betSlot: toNumber(item.betSlot) ?? toNumber(item.slot) ?? undefined,
    isBusted: !hasCashout && (bustMultiplier ?? 0) > 0,
  };
}

function toGroupFromNestedRound(item: RawRound): BetHistoryGroup | null {
  const roundId = item.roundId ?? item.roundNo ?? item.id;
  if (roundId == null) return null;

  const rawBets = Array.isArray(item.bets) ? (item.bets as RawRound[]) : [];
  const bets = rawBets
    .map(bet =>
      bet && typeof bet === 'object' ? normalizeNestedBet(item, bet) : null
    )
    .filter((row): row is BetHistoryRound => row != null)
    .sort((a, b) => (a.betSlot ?? 0) - (b.betSlot ?? 0));

  return {
    roundId: String(roundId),
    roundNo: toNumber(item.roundNo) ?? undefined,
    timestamp: parseTimestamp(item.playedAt),
    startBalance: toNumber(item.startBalance),
    endBalance: toNumber(item.endBalance),
    totalBet: toNumber(item.totalBet),
    totalWin: toNumber(item.totalWin),
    tableNo: toNumber(item.tableNo) ?? undefined,
    bets,
  };
}

function toGroupFromLegacyRound(item: RawRound): BetHistoryGroup | null {
  const bet = normalizeLegacyRound(item);
  if (!bet) return null;

  return {
    roundId: bet.roundId,
    roundNo: bet.roundNo,
    timestamp: bet.timestamp,
    startBalance: bet.startBalance,
    endBalance: bet.endBalance,
    totalBet: bet.betAmount,
    totalWin: bet.cashOutAmount ?? 0,
    tableNo: bet.tableNo,
    bets: [bet],
  };
}

function normalizePayloadItem(item: RawRound): BetHistoryGroup | null {
  return isNestedRoundPayload(item)
    ? toGroupFromNestedRound(item)
    : toGroupFromLegacyRound(item);
}

function extractRoundsPayload(data: unknown): RawRound[] {
  if (Array.isArray(data)) return data as RawRound[];
  if (!data || typeof data !== 'object') return [];

  const obj = data as Record<string, unknown>;
  const candidates = [
    obj.content,
    obj.rounds,
    obj.items,
    obj.bets,
    (obj.data as Record<string, unknown> | undefined)?.content,
    (obj.data as Record<string, unknown> | undefined)?.rounds,
    (obj.data as Record<string, unknown> | undefined)?.bets,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as RawRound[];
  }

  return [];
}

function _sortHistoryRows(a: BetHistoryRound, b: BetHistoryRound): number {
  const roundDiff = (b.roundNo ?? 0) - (a.roundNo ?? 0);
  if (roundDiff !== 0) return roundDiff;
  if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
  return (a.betSlot ?? 0) - (b.betSlot ?? 0);
}

export class BetHistoryService {
  private static instance: BetHistoryService;
  private restClient: RestClient;

  public static getInstance(): BetHistoryService {
    if (!BetHistoryService.instance) {
      BetHistoryService.instance = new BetHistoryService();
    }
    return BetHistoryService.instance;
  }

  private constructor() {
    this.restClient = new RestClient(API_CONFIG.baseUrl);
  }

  public resolveSessionId(): string | null {
    return getGameSessionId();
  }

  public async getSessionRounds(
    sessionId?: string
  ): Promise<BetHistoryGroup[]> {
    const resolvedSessionId = sessionId ?? this.resolveSessionId();
    if (!resolvedSessionId) {
      throw new Error('No active game session for bet history.');
    }

    const token = resolveAccessToken();
    if (!token) {
      throw new Error('Cannot load bet history without an access token.');
    }

    const path = API_CONFIG.endpoints.betHistoryRounds(resolvedSessionId);
    try {
      const data = await this.restClient.get<unknown>(path, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      return extractRoundsPayload(data)
        .map(normalizePayloadItem)
        .filter((row): row is BetHistoryGroup => row != null)
        .sort((a, b) => {
          const roundDiff = (b.roundNo ?? 0) - (a.roundNo ?? 0);
          if (roundDiff !== 0) return roundDiff;
          return b.timestamp - a.timestamp;
        });
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { code?: string; message?: string } };
      };
      const code = axiosError.response?.data?.code;
      if (code === 'SESSION_NOT_FOUND') {
        clearGameSession();
        throw new Error(
          'This game session has ended. Close and reopen the game to refresh bet history.'
        );
      }
      throw error;
    }
  }
}
